import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Trash2, Award, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'react-toastify';

const REGISTRIES = ['Gold Standard', 'Verra VCS', 'American Carbon Registry (ACR)', 'Climate Action Reserve (CAR)', 'California ARB', 'Other'];
const PROJECT_TYPES = ['Renewable Energy', 'Forest Conservation (REDD+)', 'Reforestation/Afforestation', 'Methane Capture', 'Energy Efficiency', 'Clean Cookstoves', 'Blue Carbon', 'Soil Carbon', 'Direct Air Capture', 'Other'];

function GHGCredits() {
  const [credits, setCredits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    transaction_type: 'purchase', registry: '', project_name: '', project_id: '',
    project_type: '', vintage_year: new Date().getFullYear(), quantity_mt: '',
    price_per_mt: '', transaction_date: new Date().toISOString().split('T')[0],
    retirement_purpose: '', serial_numbers: '', notes: ''
  });

  useEffect(() => { loadCredits(); }, []);

  const loadCredits = async () => {
    try {
      const res = await api.get('/ghg/credits');
      setCredits(res.data.credits || []);
    } catch (e) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await api.post('/ghg/credits', form);
      toast.success('Credit recorded');
      setShowForm(false);
      setForm({ transaction_type: 'purchase', registry: '', project_name: '', project_id: '', project_type: '', vintage_year: new Date().getFullYear(), quantity_mt: '', price_per_mt: '', transaction_date: new Date().toISOString().split('T')[0], retirement_purpose: '', serial_numbers: '', notes: '' });
      loadCredits();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    await api.delete(`/ghg/credits/${id}`);
    loadCredits();
  };

  const purchased = credits.filter(c => c.transaction_type === 'purchase').reduce((sum, c) => sum + parseFloat(c.quantity_mt || 0), 0);
  const retired = credits.filter(c => c.transaction_type === 'retirement').reduce((sum, c) => sum + parseFloat(c.quantity_mt || 0), 0);
  const balance = purchased - retired;
  const totalSpent = credits.filter(c => c.transaction_type === 'purchase').reduce((sum, c) => sum + parseFloat(c.total_cost || 0), 0);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: '#0A1628', margin: '0 0 4px' }}>Carbon Credits</h1>
          <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>Track carbon credit purchases and retirements</div>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Record Credit
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Purchased', value: `${purchased.toLocaleString()} mt`, color: '#16A34A', bg: '#F0FDF4', icon: TrendingUp },
          { label: 'Retired', value: `${retired.toLocaleString()} mt`, color: '#D97706', bg: '#FFFBEB', icon: TrendingDown },
          { label: 'Balance', value: `${balance.toLocaleString()} mt`, color: '#0066FF', bg: '#EFF6FF', icon: Award },
          { label: 'Total Spent', value: `$${totalSpent.toLocaleString()}`, color: '#374151', bg: '#F8FAFC', icon: Award },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ background: s.bg }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <s.icon size={14} color={s.color} />
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="card" style={{ padding: '20px', marginBottom: 20, border: '2px solid #16A34A' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 16 }}>Record Carbon Credit</div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            {['purchase', 'retirement'].map(t => (
              <button key={t} onClick={() => setForm({...form, transaction_type: t})}
                style={{ padding: '8px 20px', borderRadius: 8, border: `2px solid ${form.transaction_type === t ? '#16A34A' : '#E2E8F0'}`, background: form.transaction_type === t ? '#F0FDF4' : 'white', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: form.transaction_type === t ? 700 : 400, color: form.transaction_type === t ? '#15803D' : '#374151', textTransform: 'capitalize' }}>
                {t}
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            {[
              { label: 'Registry', key: 'registry', type: 'select', options: REGISTRIES.map(r => ({ value: r, label: r })) },
              { label: 'Project Type', key: 'project_type', type: 'select', options: PROJECT_TYPES.map(p => ({ value: p, label: p })) },
              { label: 'Project Name', key: 'project_name', type: 'text', placeholder: 'e.g. Amazon Basin REDD+' },
              { label: 'Project / Serial ID', key: 'project_id', type: 'text', placeholder: 'Registry ID' },
              { label: 'Vintage Year', key: 'vintage_year', type: 'number', placeholder: '2024' },
              { label: 'Transaction Date', key: 'transaction_date', type: 'date' },
              { label: 'Quantity (mt CO2e)', key: 'quantity_mt', type: 'number', placeholder: '100' },
              { label: 'Price per mt ($)', key: 'price_per_mt', type: 'number', placeholder: '15.00' },
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>{field.label}</label>
                {field.type === 'select' ? (
                  <select value={form[field.key]} onChange={e => setForm({...form, [field.key]: e.target.value})}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                    <option value="">Select...</option>
                    {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input type={field.type} value={form[field.key]} onChange={e => setForm({...form, [field.key]: e.target.value})}
                    placeholder={field.placeholder}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box' }} />
                )}
              </div>
            ))}
          </div>
          {form.transaction_type === 'retirement' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Retirement Purpose</label>
              <input type="text" value={form.retirement_purpose} onChange={e => setForm({...form, retirement_purpose: e.target.value})}
                placeholder="e.g. Offsetting 2024 Scope 1 emissions"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowForm(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0', background: 'white', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14 }}>Cancel</button>
            <button onClick={handleSubmit} className="btn btn-primary">Save</button>
          </div>
        </div>
      )}

      {/* Credits table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Date', 'Type', 'Registry', 'Project', 'Vintage', 'Quantity (mt)', 'Price/mt', 'Total Cost', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #F1F5F9' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {credits.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                  No carbon credit records yet. Click "Record Credit" to add your first entry.
                </td>
              </tr>
            ) : credits.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #F8FAFC' }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{c.transaction_date ? new Date(c.transaction_date).toLocaleDateString() : '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: c.transaction_type === 'purchase' ? '#F0FDF4' : '#FFFBEB', color: c.transaction_type === 'purchase' ? '#16A34A' : '#D97706', textTransform: 'capitalize' }}>{c.transaction_type}</span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{c.registry || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{c.project_name || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{c.vintage_year || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)', color: '#0066FF' }}>{parseFloat(c.quantity_mt).toLocaleString()}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{c.price_per_mt ? `$${parseFloat(c.price_per_mt).toFixed(2)}` : '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{c.total_cost ? `$${parseFloat(c.total_cost).toLocaleString()}` : '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <button onClick={() => handleDelete(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4 }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GHGCredits;
